import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldOff, ArrowRight } from "lucide-react";

/**
 * V29 — Full-screen "your profile has been deleted" notice.
 *
 * Listens for the global `tutor:profile-deleted` CustomEvent dispatched by the
 * tutorApi interceptor when the backend returns 410 GONE. Renders a sticky
 * overlay that blocks the dashboard until the tutor acknowledges, then
 * redirects to /tutor/login (per the spec).
 *
 * Mounted once near the top of <App />, beside the route tree.
 */
export default function TutorDeletedOverlay() {
    const [visible, setVisible] = useState(false);
    const [message, setMessage] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const handler = (e) => {
            setMessage(e.detail?.message || "Your tutor profile is no longer active.");
            setVisible(true);
        };
        window.addEventListener("tutor:profile-deleted", handler);
        return () => window.removeEventListener("tutor:profile-deleted", handler);
    }, []);

    if (!visible) return null;

    const onAck = () => {
        setVisible(false);
        navigate("/tutor/login", { replace: true });
    };

    return (
        <div
            className="fixed inset-0 z-[150] bg-gradient-to-br from-slate-950 via-slate-900 to-rose-950 text-white flex items-center justify-center p-4 animate-in fade-in duration-200"
            data-testid="tutor-deleted-overlay"
            role="alertdialog"
            aria-modal="true"
        >
            <div className="max-w-lg w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center shadow-2xl">
                <div className="mx-auto w-16 h-16 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center mb-4">
                    <ShieldOff className="w-8 h-8 text-rose-300" />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-rose-300/80 mb-2">Access Revoked</div>
                <h1 className="text-2xl font-bold mb-3">Your Tutor Profile Has Been Deleted</h1>
                <p className="text-sm text-slate-200 leading-relaxed mb-6" data-testid="tutor-deleted-message">
                    {message}
                </p>
                <button
                    onClick={onAck}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-semibold shadow-lg transition"
                    data-testid="tutor-deleted-ack-btn"
                >
                    Go to Login <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
